import { useState, useEffect } from 'react';
import { Save, Info } from 'lucide-react';
import { toast } from 'sonner';
import { useCompanySettings, useUpdateCompanySettings } from '@/api/queries/settings.queries';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';

const DEFAULTS = [
  { id: 'sc-01', name: 'Basic Salary', type: 'EARNING', calc: 'PERCENT_OF_CTC', value: 40, taxable: true, pf_applicable: true },
  { id: 'sc-02', name: 'House Rent Allowance', type: 'EARNING', calc: 'PERCENT_OF_BASIC', value: 50, taxable: false, pf_applicable: false },
  { id: 'sc-03', name: 'Conveyance Allowance', type: 'EARNING', calc: 'FIXED', value: 1600, taxable: false, pf_applicable: false },
  { id: 'sc-04', name: 'Special Allowance', type: 'EARNING', calc: 'BALANCE', value: 0, taxable: true, pf_applicable: false },
  { id: 'sc-05', name: 'Provident Fund (Employee)', type: 'DEDUCTION', calc: 'PERCENT_OF_BASIC', value: 12, taxable: false, pf_applicable: false },
  { id: 'sc-06', name: 'ESIC (Employee)', type: 'DEDUCTION', calc: 'PERCENT_OF_GROSS', value: 0.75, taxable: false, pf_applicable: false },
  { id: 'sc-07', name: 'Professional Tax', type: 'DEDUCTION', calc: 'FIXED', value: 200, taxable: false, pf_applicable: false },
];

const CALC_LABELS = { PERCENT_OF_CTC: '% of CTC', PERCENT_OF_BASIC: '% of Basic', PERCENT_OF_GROSS: '% of Gross', FIXED: 'Fixed ₹', BALANCE: 'Balance (auto)' };

function Toggle({ on, onToggle }) {
  return (
    <button onClick={onToggle} className={`w-9 h-5 rounded-full transition-colors relative ${on ? 'bg-[var(--accent)]' : 'bg-[var(--border)]'}`}>
      <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${on ? 'left-4' : 'left-0.5'}`}/>
    </button>
  );
}

export default function SalaryComponentsSettingsPage() {
  const { data } = useCompanySettings();
  const { mutate, isPending } = useUpdateCompanySettings();
  const [components, setComponents] = useState(DEFAULTS);

  useEffect(() => {
    const saved = data?.data?.settings?.salary_components;
    if (saved?.length) setComponents(saved);
  }, [data]);

  const update = (id, k, v) => setComponents(cs => cs.map(c => c.id === id ? { ...c, [k]: v } : c));
  const numCls = "h-8 px-2 w-20 text-center bg-[var(--bg-muted)] border border-[var(--border)] rounded-[8px] text-[13px] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]";

  const earnings = components.filter(c => c.type === 'EARNING');
  const deductions = components.filter(c => c.type === 'DEDUCTION');

  const Section = ({ title, items }) => (
    <div className="card p-0 overflow-x-auto">
      <div className="px-5 py-3 border-b border-[var(--border)]">
        <h3 className="text-[13px] font-bold text-[var(--text-primary)]">{title}</h3>
      </div>
      <table className="w-full">
        <thead><tr className="border-b border-[var(--border)]">
          {['Component','Calculation','Value','Taxable','PF Applicable'].map(h => (
            <th key={h} className="text-left text-[11px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)] px-5 py-2.5">{h}</th>
          ))}
        </tr></thead>
        <tbody className="divide-y divide-[var(--border)]">
          {items.map(c => (
            <tr key={c.id} className="hover:bg-[var(--bg-muted)]">
              <td className="px-5 py-3 text-[13px] font-semibold text-[var(--text-primary)]">{c.name}</td>
              <td className="px-5 py-3 text-[12px] text-[var(--text-secondary)]">{CALC_LABELS[c.calc]}</td>
              <td className="px-5 py-3">
                {c.calc !== 'BALANCE'
                  ? <input type="number" min="0" step="0.01" value={c.value} onChange={e => update(c.id,'value',+e.target.value)} className={numCls}/>
                  : <span className="text-[12px] text-[var(--text-tertiary)]">Auto</span>
                }
              </td>
              <td className="px-5 py-3"><Toggle on={c.taxable} onToggle={() => update(c.id,'taxable',!c.taxable)}/></td>
              <td className="px-5 py-3"><Toggle on={c.pf_applicable} onToggle={() => update(c.id,'pf_applicable',!c.pf_applicable)}/></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const handleSave = () => {
    mutate({ settings: { salary_components: components } }, {
      onSuccess: () => toast.success('Salary components saved'),
      onError: () => toast.error('Failed to save'),
    });
  };

  return (
    <div className="space-y-5 fade-in">
      <PageHeader title="Salary Components" breadcrumb={[{ label: 'Settings', href: '/settings' }, { label: 'Salary Components' }]}/>
      <div className="flex items-start gap-2 px-4 py-3 rounded-[10px] bg-blue-50 dark:bg-blue-900/20 border border-blue-200 text-[13px] text-blue-700">
        <Info size={14} className="mt-0.5 shrink-0"/>
        <p>Changes here affect all new salary structures. Existing structures are not modified automatically.</p>
      </div>
      <Section title="Earnings" items={earnings}/>
      <Section title="Deductions" items={deductions}/>
      <Button icon={Save} onClick={handleSave} disabled={isPending}>{isPending ? 'Saving…' : 'Save Components'}</Button>
    </div>
  );
}
